/**
 * DashboardScreen — visão geral do tenant no visual Sphere (imagem aprovada).
 *
 * Composição: hero com a esfera WebGL → cinco indicadores → Mission Control +
 * Consumo de budget → Projetos recentes. Dados REAIS:
 *   - useDashboardKpis({ mes }) → GET /dashboard/kpis?mes=YYYY-MM (o mês vem do
 *     filtro de período do painel de budget; padrão = mês corrente no fuso da app)
 *   - useRecentProjects()       → GET /dashboard/projects
 *   - useBudget / useSetBudget  → GET|PUT /dashboard/budget
 *   - useMissao(execução ativa) → trace + delivery-status da execução em andamento
 * Sem dados inventados: ausente = "—", carregando = skeleton, erro = aviso com retry.
 *
 * Ações preservadas: Novo projeto, Abrir (por status), Executar, Regenerar código
 * (popup de confirmação), Parar e Excluir (confirmação), espera de indexação (409),
 * selo de revisão humana, Editar orçamento (403 explicado).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Activity, Zap, Database, DollarSign, Shield, ArrowRight, Ellipsis, FolderKanban, GitBranch,
  Play, RotateCcw, ExternalLink, Square, Trash2, ArrowUp, ArrowDown, RefreshCw, StepForward,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { ConfirmDialog } from '@/components/ui';
import { useNavigationContext } from '@/navigation';
import { useAuth } from '@/auth/AuthContext';
import {
  useRecentProjects, useDashboardKpis, useBudget, useSetBudget,
} from '@/api/dashboard';
import {
  useStartExecution, useStopProject, useDeleteProject, useRegenerateCode, useContinueExecution,
} from '@/api/executions';
import { clearJourneyState } from '@/screens/journey/JourneyContext';
import { useIniciarAguardandoIndexacao } from '@/api/aguardarIndexacao';
import { AvisoIndexacao } from '@/components/AvisoIndexacao';
import { SeloDeGarantia } from '@/components/SeloDeGarantia';
import { projectRoute } from '@/lib/projectRoute';
import {
  usd, dataHora, inteiro, mesAtual, numeroOuNulo, rotuloDoMes, tokensCompactos, variacao,
} from '@/lib/format';
import { HeroSphere } from '@/ui/sphere/SphereCanvas';
import { MissionPanel } from '@/ui/sphere/MissionPanel';
import { MetricCard, BudgetPanel, Sparkline } from '@/ui/sphere/DashboardParts';
import { useMissao } from '@/ui/sphere/useMissao';
import { usePopover, moverFocoNoMenu } from '@/ui/sphere/usePopover';

// ─── Status do projeto (texto + cor; nunca só cor) ────────────────────────────

const STATUS_DO_PROJETO = {
  running: { label: 'EM EXECUÇÃO', cor: () => C.digital },
  'gate-waiting': { label: 'AGUARDANDO GATE', cor: () => C.warn },
  completed: { label: 'CONCLUÍDO', cor: () => C.cyber },
  failed: { label: 'FALHOU', cor: () => C.hot },
};

function StatusDoProjeto({ status }) {
  const s = STATUS_DO_PROJETO[status] ?? { label: String(status || 'rascunho').replace(/-/g, ' ').toUpperCase(), cor: () => C.neutralMuted };
  const cor = s.cor();
  return (
    <span role="status" aria-label={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: cor, fontWeight: 600, letterSpacing: '.04em' }}>
      <span aria-hidden="true" className="sp-dot" style={{ background: cor }} />
      {s.label}
    </span>
  );
}

// ─── Menu de ações de uma linha ───────────────────────────────────────────────

function AcoesDoProjeto({ p, acoes }) {
  const pop = usePopover();
  useEffect(() => {
    if (pop.open) pop.panelRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [pop.open, pop.panelRef]);
  const itens = acoes.filter(Boolean);
  const run = (fn) => { pop.close(true); fn(); };
  return (
    <>
      <button
        ref={pop.triggerRef}
        type="button"
        className="sp-project-menu-btn"
        aria-label={`Ações de ${p.name}`}
        aria-haspopup="menu"
        aria-expanded={pop.open}
        onClick={pop.toggle}
      >
        <Ellipsis size={20} strokeWidth={1.75} aria-hidden="true" />
      </button>
      {pop.open && (
        <div
          ref={pop.panelRef}
          className="sp-popover sp-project-menu"
          role="menu"
          aria-label={`Ações de ${p.name}`}
          onKeyDown={(e) => moverFocoNoMenu(e, pop.panelRef.current)}
        >
          {itens.map((a) => (
            <button
              key={a.key}
              type="button"
              role="menuitem"
              className={`sp-popover-item${a.perigo ? ' sp-popover-item--danger' : ''}`}
              aria-label={a.ariaLabel}
              title={a.title}
              disabled={a.disabled}
              onClick={() => run(a.onClick)}
            >
              <a.icon size={16} strokeWidth={1.75} aria-hidden="true" /> {a.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Lista de projetos ────────────────────────────────────────────────────────

function ProjectList({ projects, navigate }) {
  // Regra dos hooks: todos antes do retorno antecipado da lista vazia.
  const startExec  = useStartExecution();
  const stopProj   = useStopProject();
  const deleteProj = useDeleteProject();
  const regenCode  = useRegenerateCode();
  const continuar  = useContinueExecution();
  // 409 "contexto não indexado": espera visível + repetição automática.
  const { iniciar: iniciarExec, estado: indexacao } =
    useIniciarAguardandoIndexacao(startExec.mutateAsync);
  const aguardandoIndice = indexacao.fase === 'indexando';
  const { iniciar: iniciarRegen, estado: indexacaoRegen } =
    useIniciarAguardandoIndexacao(regenCode.mutateAsync);
  const aguardandoIndiceRegen = indexacaoRegen.fase === 'indexando';

  // Regenerar: popup avisando que TUDO será gerado conforme o que já está salvo.
  const [regenTarget, setRegenTarget] = useState(null);
  // Continuar de onde parou: aproveita o que a última execução já fechou.
  const [continuarTarget, setContinuarTarget] = useState(null);
  const [continuarErro, setContinuarErro] = useState(null);   // { id, texto }
  const [stopTarget, setStopTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  if (!projects?.length) {
    return (
      <div className="sp-project-empty">
        Nenhum projeto encontrado. Inicie uma nova jornada.
      </div>
    );
  }

  const doRegenerate = async () => {
    const p = regenTarget;
    if (!p) return;
    // Fecha o popup já: a espera pela indexação (se houver) aparece na linha.
    setRegenTarget(null);
    try {
      const res = await iniciarRegen(p.id);
      const eid = res?.execution_id;
      navigate(`/execution/liveboard${eid ? `?execution=${eid}` : ''}`);
    } catch { setRegenTarget(null); /* erro no estado da mutation */ }
  };

  const doContinuar = async () => {
    const p = continuarTarget;
    if (!p) return;
    setContinuarTarget(null);
    setContinuarErro(null);
    try {
      const res = await continuar.mutateAsync(p.id);
      const eid = res?.execution_id;
      navigate(`/execution/liveboard${eid ? `?execution=${eid}` : ''}`);
    } catch (err) {
      // o motivo vem escrito pela API (ex.: ainda não há execução encerrada de onde continuar)
      setContinuarErro({ id: p.id, texto: err?.detail || err?.message
        || 'Não foi possível continuar de onde a execução anterior parou.' });
    }
  };

  // Para o projeto e qualquer execução ativa (corta o gasto de tokens na hora).
  const doStop = async () => {
    const p = stopTarget;
    setStopTarget(null);
    if (!p) return;
    try { await stopProj.mutateAsync(p.id); } catch { /* erro no estado da mutation */ }
  };

  // Exclui o projeto (soft-delete). O backend para execuções ativas antes.
  const doDelete = async () => {
    const p = deleteTarget;
    setDeleteTarget(null);
    if (!p) return;
    try { await deleteProj.mutateAsync(p.id); } catch { /* erro no estado da mutation */ }
  };

  // Inicia o pipeline (Temporal) e navega para o Live Board acompanhar.
  const launch = async (p) => {
    try {
      const res = await iniciarExec(p.id);
      const eid = res?.execution_id;
      navigate(`/execution/liveboard${eid ? `?execution=${eid}` : ''}`);
    } catch { /* erro tratado pelo estado da mutation */ }
  };

  const openProject = (p) => navigate(projectRoute(p));

  return (
    <>
      <ul className="sp-project-list" aria-label="Projetos recentes">
        {projects.map((p) => {
          const ativo = p.status === 'running' || p.status === 'gate-waiting';
          const podeExecutar = !ativo && p.status !== 'completed';
          const esperandoExec = aguardandoIndice && indexacao.alvo === p.id;
          const esperandoRegen = aguardandoIndiceRegen && indexacaoRegen.alvo === p.id;
          const acoes = [
            podeExecutar && {
              key: 'run', icon: Play, onClick: () => launch(p),
              label: esperandoExec ? 'Aguardando indexação…' : startExec.isPending ? 'Iniciando...' : 'Executar',
              ariaLabel: `Executar pipeline de ${p.name}`,
              disabled: startExec.isPending || aguardandoIndice,
            },
            p.temporal_workflow_id && !ativo && {
              key: 'continuar', icon: StepForward, onClick: () => setContinuarTarget(p),
              label: continuar.isPending ? 'Continuando...' : 'Continuar de onde parou',
              ariaLabel: `Continuar a última execução de ${p.name} de onde ela parou`,
              title: 'Aproveita o que a última execução já fechou e roda só o que falhou ou faltou',
              disabled: continuar.isPending || regenCode.isPending,
            },
            p.temporal_workflow_id && !ativo && {
              key: 'regen', icon: RotateCcw, onClick: () => setRegenTarget(p),
              label: esperandoRegen ? 'Aguardando indexação…' : regenCode.isPending ? 'Regenerando...' : 'Regenerar código',
              ariaLabel: `Regenerar o código de ${p.name}`,
              title: 'Reaproveita a definição e o design; pula os gates e gera o código de novo',
              disabled: regenCode.isPending || aguardandoIndiceRegen,
            },
            p.temporal_workflow_id && {
              key: 'saude', icon: Activity,
              onClick: () => navigate(`/execution/liveboard?execution=${p.temporal_workflow_id}&aba=saude`),
              label: 'Saúde da execução',
              ariaLabel: `Ver a saúde da última execução de ${p.name}`,
              title: 'Etapas, alertas e o que fazer — da execução em andamento ou da última encerrada',
            },
            {
              key: 'open', icon: ExternalLink, onClick: () => openProject(p),
              label: 'Abrir', ariaLabel: `Abrir projeto ${p.name}`,
            },
            ativo && {
              key: 'stop', icon: Square, onClick: () => setStopTarget(p),
              label: stopProj.isPending ? 'Parando...' : 'Parar', ariaLabel: `Parar projeto ${p.name}`,
              title: 'Parar o projeto e a execução ativa', disabled: stopProj.isPending,
            },
            {
              key: 'delete', icon: Trash2, onClick: () => setDeleteTarget(p), perigo: true,
              label: deleteProj.isPending ? 'Excluindo...' : 'Excluir', ariaLabel: `Excluir projeto ${p.name}`,
              title: 'Excluir o projeto (encerra execuções ativas)', disabled: deleteProj.isPending,
            },
          ];
          return (
            <li key={p.id} className="sp-project">
              <span className="sp-project-icon" aria-hidden="true"><FolderKanban size={20} strokeWidth={1.75} /></span>
              <div className="sp-project-name">
                <button type="button" onClick={() => openProject(p)} title={`Abrir ${p.name}`}>
                  {p.name}
                </button>
                <small>
                  <GitBranch size={12} strokeWidth={1.75} aria-hidden="true" />
                  <span title={p.repo || undefined}>{p.repo || 'Sem repositório'}</span>
                </small>
              </div>
              <div className="sp-project-status">
                <StatusDoProjeto status={p.status} />
                {p.assurance && <SeloDeGarantia estado={p.assurance} compacto />}
                {/* Multi-repo: repos VERIFICADOS (verdes) / total — rodam em PARALELO. */}
                {p.repo_progress && (
                  <span className="sp-project-extra" title="Repositórios verificados (verdes) / total — geração multi-repo em paralelo">
                    {p.repo_progress} repos verificados
                  </span>
                )}
                {(esperandoExec || esperandoRegen) && (
                  <span className="sp-project-extra" role="status">Aguardando indexação…</span>
                )}
              </div>
              <div className="sp-project-field" data-field="started">
                <small>Iniciado</small>{p.started_at ? dataHora(p.started_at) : (p.started ?? '—')}
              </div>
              <div className="sp-project-field" data-field="completed">
                <small>Concluído em</small>{p.completed_at ? dataHora(p.completed_at) : (p.completed ?? '—')}
              </div>
              <div className="sp-project-field" data-field="tokens">
                <small>Tokens</small>{p.tokens_total != null ? tokensCompactos(p.tokens_total) : (p.tokens ?? '—')}
              </div>
              <div className="sp-project-field" data-field="cost">
                <small>Custo</small>{p.cost_total != null ? usd(p.cost_total) : (p.cost ?? '—')}
              </div>
              <div style={{ position: 'relative' }}>
                <AcoesDoProjeto p={p} acoes={acoes} />
              </div>
              {indexacao.alvo === p.id && (
                <div className="sp-project-notice">
                  <AvisoIndexacao estado={indexacao} onTentarDeNovo={() => launch(p)} />
                </div>
              )}
              {indexacaoRegen.alvo === p.id && (
                <div className="sp-project-notice">
                  <AvisoIndexacao estado={indexacaoRegen} onTentarDeNovo={() => setRegenTarget(p)} />
                </div>
              )}
              {continuarErro?.id === p.id && (
                <div className="sp-project-notice" role="alert" data-testid="continuar-erro"
                  style={{ color: C.warn, fontSize: 12.5 }}>
                  {continuarErro.texto}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={Boolean(continuarTarget)}
        title="Continuar de onde parou?"
        confirmLabel="Continuar"
        busy={continuar.isPending}
        onCancel={() => setContinuarTarget(null)}
        onConfirm={doContinuar}
        message={
          <>
            Vou continuar a última execução de{' '}
            <strong style={{ color: C.neutral }}>{continuarTarget?.name}</strong> em vez de gerar tudo de
            novo. O escopo, o contrato aprovado e o design são <strong>os mesmos</strong>.
            {'\n\n'}
            <strong>O que já fechou fica como está</strong> — as etapas aprovadas não rodam de novo
            e não são cobradas outra vez.
            {'\n'}
            <strong>O que ficou com pendência volta ao portão</strong> — primeiro a conferência é
            repetida; o agente só é chamado se ela reprovar, e recebe a lista exata do que corrigir.
            {'\n'}
            <strong>O que não chegou a ser construído roda agora</strong>, e a verificação final é
            feita sobre o conjunto inteiro.
            {'\n\n'}
            Se a execução anterior não deixou nada para aproveitar, eu aviso no Live Board e a
            construção começa do zero. Para refazer tudo de propósito, use “Regenerar código”.
          </>
        }
      />
      <ConfirmDialog
        open={Boolean(regenTarget)}
        title="Gerar o código novamente?"
        confirmLabel="Gerar novamente"
        busy={regenCode.isPending}
        onCancel={() => setRegenTarget(null)}
        onConfirm={doRegenerate}
        message={
          <>
            Vou gerar TODO o código de <strong style={{ color: C.neutral }}>{regenTarget?.name}</strong> de
            novo, <strong>conforme as definições já salvas</strong> — escopo de negócio, blueprint técnico,
            squad e design aprovados. Os Quality Gates são <strong>auto-aprovados</strong> (sem revisão humana)
            e uma nova execução começa do zero no sandbox real. Nada da execução anterior é
            aproveitado — se ela só falhou numa etapa, “Continuar de onde parou” é mais rápido.
            {'\n\n'}
            Se o projeto tem múltiplos repositórios e o modo multi-repo está ligado, será aberto
            <strong> um Pull Request por repositório</strong> tocado.
          </>
        }
      />
      <ConfirmDialog
        open={Boolean(stopTarget)}
        title="Parar o projeto?"
        tone="danger"
        confirmLabel="Parar"
        busy={stopProj.isPending}
        onCancel={() => setStopTarget(null)}
        onConfirm={doStop}
        message={`Parar o projeto "${stopTarget?.name ?? ''}" e qualquer execução em andamento?`}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Excluir o projeto?"
        tone="danger"
        confirmLabel="Excluir"
        busy={deleteProj.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={doDelete}
        message={`Excluir o projeto "${deleteTarget?.name ?? ''}"?\n\nEle sai da lista e qualquer execução ativa é encerrada. Esta ação não pode ser desfeita pela interface.`}
      />
    </>
  );
}

// ─── Editor do orçamento (mesma regra de antes) ───────────────────────────────

function EditorDeOrcamento({ inicial, setBudget, currency, onFechar }) {
  const [valor, setValor] = useState(inicial);
  const [invalido, setInvalido] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const salvar = () => {
    const amount = parseFloat(String(valor).replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0) { setInvalido(true); return; }
    setInvalido(false);
    setBudget.mutate({ monthlyAmount: amount, currency: currency ?? 'USD' }, { onSuccess: onFechar });
  };
  const erroId = 'budget-input-erro';
  return (
    <div style={{ marginTop: 10 }}>
      <div className="sp-budget-edit">
        <label htmlFor="budget-input">
          Orçamento mensal (US$)
          <input
            ref={inputRef}
            id="budget-input"
            className="sp-input"
            type="text"
            inputMode="decimal"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salvar(); if (e.key === 'Escape') onFechar(); }}
            placeholder="5000"
            aria-invalid={invalido || undefined}
            aria-describedby={invalido || setBudget.isError ? erroId : undefined}
            style={{ width: 150 }}
          />
        </label>
        <button type="button" className="sp-btn sp-btn--primary" onClick={salvar} disabled={setBudget.isPending}>
          {setBudget.isPending ? 'Salvando...' : 'Salvar'}
        </button>
        <button type="button" className="sp-btn" onClick={onFechar}>Cancelar</button>
      </div>
      {(invalido || setBudget.isError) && (
        <div id={erroId} className="sp-field-error" role="alert">
          {invalido
            ? 'Informe um valor numérico maior ou igual a zero.'
            : setBudget.error?.status === 403
              ? 'Sem permissão para editar o orçamento (requer Admin ou Manager).'
              : `Falha ao salvar: ${String(setBudget.error?.message || setBudget.error)}`}
        </div>
      )}
    </div>
  );
}

/** Os 12 meses até o corrente (mais recente primeiro) para o seletor de período. */
function opcoesDePeriodo(corrente) {
  const [y, m] = corrente.split('-').map(Number);
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

// ─── DashboardScreen ──────────────────────────────────────────────────────────

export function DashboardScreen() {
  const { navigate } = useNavigationContext();
  const { user }     = useAuth();

  const mesCorrente = useMemo(() => mesAtual(), []);
  const [periodo, setPeriodo] = useState(mesCorrente);
  const doPeriodoAtual = periodo === mesCorrente;

  const kpisQuery     = useDashboardKpis({ mes: doPeriodoAtual ? null : periodo });
  const projectsQuery = useRecentProjects();
  const budgetQuery   = useBudget();
  const setBudget     = useSetBudget();

  const kpis     = kpisQuery.data ?? null;
  const projects = projectsQuery.data;
  const indisp   = new Set(kpis?.indisponiveis ?? []);
  const carregandoKpis = kpisQuery.isLoading;

  const valor = (campo, grupo) => (kpis && !(grupo && indisp.has(grupo)) ? numeroOuNulo(kpis[campo]) : null);
  const fluxosAtivos = valor('fluxosAtivos', 'fluxos');
  const fluxosMes    = valor('fluxosMes', 'fluxos');
  const tokensMes    = valor('tokensMesValor', 'fluxos');
  const custoMes     = valor('custoMesValor', 'fluxos');
  const gates        = valor('gateAprovacoes', 'gates');
  const varFluxos    = valor('variacaoFluxosPct', 'fluxos');
  const varCusto     = valor('variacaoCustoPct', 'fluxos');
  const limite       = numeroOuNulo(budgetQuery.data?.monthlyAmount) ?? valor('budgetTotalValor');
  const serie        = Array.isArray(kpis?.custoSerie) ? kpis.custoSerie : null;

  // Execução exibida no Mission Control: a mais recente EM ANDAMENTO (running /
  // aguardando gate). Sem nenhuma → estado ocioso (contagens globais continuam).
  const ativa = (projects ?? []).find((p) => (p.status === 'running' || p.status === 'gate-waiting') && p.temporal_workflow_id) ?? null;
  const missao = useMissao(ativa?.temporal_workflow_id ?? null, {
    activeAgents: Number.isInteger(kpis?.agentesEmExecucao) ? kpis.agentesEmExecucao : null,
  });

  // Edição do orçamento mensal
  const [editingBudget, setEditingBudget] = useState(false);
  const openBudgetEdit = () => { setBudget.reset(); setEditingBudget(true); };

  // Nome de saudação: primeiro nome do usuário autenticado (sem nome → sem vírgula).
  const firstName = user?.name?.trim()?.split(/\s+/)?.[0] ?? '';

  const notaPeriodo = doPeriodoAtual ? 'No mês corrente' : `Em ${rotuloDoMes(periodo, true)}`;
  const trend = (v, custo = false) => {
    if (v == null) return undefined;
    if (v === 0) return undefined;
    if (custo) return v > 0 ? 'up-bad' : 'down';
    return v > 0 ? 'up' : 'down';
  };
  const notaVariacao = (v, custo = false) => (v == null ? notaPeriodo : (
    <>
      {v > 0 ? <ArrowUp size={13} strokeWidth={2} aria-hidden="true" /> : v < 0 ? <ArrowDown size={13} strokeWidth={2} aria-hidden="true" /> : null}
      <span>{variacao(v)} vs. mês anterior</span>
    </>
  ));

  return (
    <div className="sp-dash">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="sp-hero" aria-labelledby="sp-hero-title">
        <div className="sp-hero-copy">
          <p className="sp-eyebrow">Bem-vindo de volta{firstName ? `, ${firstName}` : ''}</p>
          <h1 id="sp-hero-title">Construa o futuro<br />com <em>agentes.</em></h1>
          <p className="sp-hero-lead">
            Orquestre agentes, gere software de ponta a ponta e entregue projetos com mais
            velocidade, qualidade e controle.
          </p>
        </div>
        <HeroSphere />
        <ul className="sp-hero-rail" aria-hidden="true">
          <li>MAIS IDEIAS</li>
          <li>MAIS AGENTES</li>
          <li className="is-active">MAIS SOFTWARE</li>
          <li>MAIS IMPACTO</li>
        </ul>
      </section>

      {kpisQuery.error && !kpis && (
        <div className="sp-alert" role="alert">
          Não foi possível carregar os indicadores. {String(kpisQuery.error?.detail || kpisQuery.error?.message || '')}
          <button type="button" className="sp-btn" onClick={() => kpisQuery.refetch()} style={{ marginLeft: 'auto' }}>
            <RefreshCw size={14} strokeWidth={1.75} aria-hidden="true" /> Tentar novamente
          </button>
        </div>
      )}

      {/* ── Indicadores ──────────────────────────────────────────────────── */}
      <section className="sp-metrics" aria-label="Indicadores" style={{ marginTop: kpisQuery.error && !kpis ? 12 : 0 }}>
        <MetricCard
          label="Fluxos ativos" tone="cyan" icon={Activity} loading={carregandoKpis}
          value={fluxosAtivos == null ? null : inteiro(fluxosAtivos)}
          badge={fluxosAtivos > 0 ? <span className="sp-dot sp-dot--success" aria-hidden="true" /> : null}
          note={gates == null ? 'Execuções em andamento' : `${inteiro(gates)} ${gates === 1 ? 'gate aguardando' : 'gates aguardando'} aprovação`}
        />
        <MetricCard
          label="Fluxos no mês" tone="violet" icon={Zap} loading={carregandoKpis}
          value={fluxosMes == null ? null : inteiro(fluxosMes)}
          note={notaVariacao(varFluxos)} trend={trend(varFluxos)}
        />
        <MetricCard
          label="Tokens consumidos" tone="blue" icon={Database} loading={carregandoKpis}
          value={tokensMes == null ? null : tokensCompactos(tokensMes)}
          note={notaPeriodo}
        />
        <MetricCard
          label="Custo no mês" tone="green" icon={DollarSign} loading={carregandoKpis}
          value={custoMes == null ? null : usd(custoMes)}
          note={notaVariacao(varCusto, true)} trend={trend(varCusto, true)}
          mini={serie ? <Sparkline valores={serie.filter((m) => m.mes <= mesCorrente).map((m) => m.valor)} /> : null}
        />
        <MetricCard
          label="Gates pendentes" tone="amber" icon={Shield} loading={carregandoKpis}
          value={gates == null ? null : inteiro(gates)}
          note="Aguardando aprovação"
          onClick={() => navigate('gates')}
          ariaLabel={`Gates pendentes (${gates == null ? '—' : inteiro(gates)}) — abrir a fila de aprovação`}
        />
      </section>

      {/* ── Mission Control + Budget ─────────────────────────────────────── */}
      <div className="sp-middle">
        <MissionPanel
          snapshot={missao.snapshot}
          conexao={missao.conexao}
          acompanhando={ativa ? ativa.name : null}
          counts={{
            activeAgents: indisp.has('missao') ? null : numeroOuNulo(kpis?.agentesEmExecucao),
            activeProjects: indisp.has('missao') ? null : numeroOuNulo(kpis?.projetosEmAndamento),
            pendingGates: gates,
          }}
          actionLabel="Abrir Mission Control"
          onAction={() => navigate(`/execution/mission-control${ativa?.temporal_workflow_id ? `?execution=${ativa.temporal_workflow_id}` : ''}`)}
          onGates={() => navigate('gates')}
          onRetry={missao.refetch}
        />
        <BudgetPanel
          gasto={custoMes}
          limite={limite}
          serie={serie}
          serieIndisponivel={Boolean(kpis) && (indisp.has('serie') || !serie)}
          loading={carregandoKpis}
          periodo={periodo}
          mesCorrente={mesCorrente}
          onPeriodo={setPeriodo}
          opcoesDePeriodo={opcoesDePeriodo(mesCorrente)}
          onEditar={openBudgetEdit}
          editor={editingBudget ? (
            <EditorDeOrcamento
              inicial={String(budgetQuery.data?.monthlyAmount ?? '')}
              currency={budgetQuery.data?.currency}
              setBudget={setBudget}
              onFechar={() => setEditingBudget(false)}
            />
          ) : null}
        />
      </div>

      {/* ── Projetos recentes ────────────────────────────────────────────── */}
      <section className="sp-panel sp-projects" aria-labelledby="sp-projects-title">
        <div className="sp-panel-header">
          <div>
            <h2 id="sp-projects-title">Projetos recentes</h2>
            <p className="sp-projects-description">Seus projetos de geração de software com agentes.</p>
          </div>
          <div className="sp-actions">
            <button type="button" className="sp-btn" onClick={() => navigate('project')}>
              Ver todos <ArrowRight size={16} strokeWidth={1.75} aria-hidden="true" />
            </button>
            <button type="button" className="sp-btn sp-btn--primary" onClick={() => { clearJourneyState(); navigate('journey'); }}>
              <Plus size={16} strokeWidth={2} aria-hidden="true" /> Novo projeto
            </button>
          </div>
        </div>

        {projectsQuery.isLoading ? (
          <div className="sp-project-empty" role="status">Carregando projetos…</div>
        ) : projectsQuery.error ? (
          <div className="sp-alert" role="alert">
            Não foi possível carregar os projetos. {String(projectsQuery.error?.detail || projectsQuery.error?.message || '')}
            <button type="button" className="sp-btn" onClick={() => projectsQuery.refetch()} style={{ marginLeft: 'auto' }}>
              <RefreshCw size={14} strokeWidth={1.75} aria-hidden="true" /> Tentar novamente
            </button>
          </div>
        ) : (
          <ProjectList projects={projects} navigate={navigate} />
        )}
      </section>
    </div>
  );
}
